import interact from 'interact.js'
import immutable from 'immutable'
import _ from 'lodash'


const EMPTY_SPACE = -1
const MIN_WIDTH = 1

export default (selector, evts) => {
    let legs = {}
    let grid = []

    let modified = new immutable.Set()

    let columnWidth = 0

    let settings = {
        selector,
        evts,
        columns: 0,
    }

    let container = null
    let ghost = null

    let printGrid = () => {
        let g = _.map(grid, function(c) {
            return c === EMPTY_SPACE ? '*' : c
        }).join('')

        console.log(`[${g}]`)
    }

    let buildGhost = (leg) => {
        ghost = {}
        ghost.x = leg.xp
        ghost.w = leg.wp
        ghost.element = leg.element.cloneNode(true)
        ghost.element.className += ' ghost'
        ghost.origIndex = leg.index
        container.appendChild(ghost.element)

        return ghost
    }

    let triggerMoveEvent = (legIndexes) => {
        let finalLegs = _.map(legIndexes.toJS(), (legIdx) => legs[legIdx])
        _.invoke(settings.evts, 'didMove', finalLegs)
    }

    let triggerResizeEvent = (legIndexes) => {
        let finalLegs = _.map(legIndexes.toJS(), (legIdx) => legs[legIdx])
        _.invoke(settings.evts, 'didResize', finalLegs)
    }

    let move = (legIdx, x) => {
        let leg = legs[legIdx]

        let pixelX = x * columnWidth
        leg.element.style.transform = `translateX(${pixelX}px)`

        leg.x = x
        leg.xp = pixelX
    }

    let resize = (legIdx, x, w) => {
        move(legIdx, x)

        let leg = legs[legIdx]
        let pixelWidth = w * columnWidth
        leg.element.style.width = `${pixelWidth}px`

        leg.w = w
        leg.wp = pixelWidth
    }

    let getResizeBounds = (leg) => {
    // figure out the bounds around this leg.
    // specifically we need:
    // start - empty space to left
    // end - empty space to right
    // prev - leg before this one
    // next - leg after this one

        let start = -1
        let end = grid.length

        // helper function that tells us if a space in the grid is filled with some other leg
        let isCellFilled = (i) => {
            return grid[i] !== leg.index && grid[i] !== EMPTY_SPACE
        }

        // find the start
        for (let i = leg.x; i >= 0; i--) {
            if (isCellFilled(i)) {
                start = i
                break
            }
        }

        // find the previous leg
        let prevIdx = grid[start]
        let prev = (_.isUndefined(prevIdx) ? null : legs[prevIdx])

        // find the end
        for (let i = leg.x + leg.w; i < grid.length; i++) {
            if (isCellFilled(i)) {
                end = i
                break
            }
        }

        // find the next leg
        let nextIdx = grid[end]
        let next = (_.isUndefined(nextIdx) ? null : legs[nextIdx])

        return [start, end, prev, next]
    }

    let updateGrid = () => {
    // reset
        grid = _.times(grid.length, _.constant(EMPTY_SPACE))

        // fill in each leg
        _.each(_.values(legs), function(leg) {
            _.fill(grid, leg.index, leg.x, leg.x + leg.w)
        })
    }

    let getCollision = (leg, x) => {
        let subgrid = _.slice(grid, x, x + leg.w)

        return _.find(subgrid, (index) => {
            return index !== EMPTY_SPACE && index !== leg.index
        })
    }

    let resolveCollision = (leg, newX, collisionIdx) => {
        let cLeg = legs[collisionIdx]
        let direction = newX - leg.x

        if (direction < 0) {
            // moving left
            let collisionDepth = (cLeg.x + cLeg.w) - newX

            if (collisionDepth > cLeg.w / 2) {
                move(leg.index, cLeg.x)
                move(cLeg.index, cLeg.x + leg.w)
            }
        } else if (direction > 0) {
            // moving right
            let collisionDepth = (newX + leg.w) - cLeg.x

            if (collisionDepth > cLeg.w / 2) {
                move(cLeg.index, cLeg.x - leg.w)
                move(leg.index, leg.x + cLeg.w)
            }
        }

        modified = modified.add(cLeg.index)
    }

    let attemptMove = (leg, newX) => {
    // can this leg fit in the grid?
        if (newX < 0 || newX + leg.w > grid.length) {
            return false
        }

        modified = modified.add(leg.index)

        // check collisions
        let collisionIdx = getCollision(leg, newX)

        if (_.isUndefined(collisionIdx)) {
            move(leg.index, newX)
        } else {
            resolveCollision(leg, newX, collisionIdx)
        }

        updateGrid()
    }

    let attemptResize = (leg, newW, newX) => {
        if (newW < MIN_WIDTH) {
            return
        }

        let start, end, prev, next
        [start, end, prev, next] = getResizeBounds(leg)

        if (_.isUndefined(newX)) {
            // right edge resize
            let newEnd = leg.x + newW

            if (newEnd <= end) {
                // simple case: it just fits
                resize(leg.index, leg.x, newW)
            } else if (next && newEnd > end) {
                // less simple case: need to shrink the next block if possible
                let depth = newEnd - next.x
                if (next.w - depth >= MIN_WIDTH) {
                    resize(next.index, newEnd, next.w - depth)
                    resize(leg.index, leg.x, newW)
                } else {
                    resize(next.index, next.x + next.w - MIN_WIDTH, MIN_WIDTH)
                    resize(leg.index, leg.x, next.x - leg.x)
                }

                modified = modified.add(next.index)
            }
        } else {
            // left edge resize

            if (newX > start) {
                // simple case: it fits
                resize(leg.index, newX, newW)
            } else if (prev && newX <= start) {
                // less simple case: resize the previous block if possible
                let depth = prev.x + prev.w - newX

                if (prev.w - depth >= MIN_WIDTH) {
                    resize(prev.index, prev.x, prev.w - depth)          
                    resize(leg.index, newX, newW)
                } else {
                    resize(prev.index, prev.x, MIN_WIDTH)
                    resize(leg.index, prev.x + MIN_WIDTH, (leg.x - (prev.x + MIN_WIDTH)) + leg.w)
                }

                modified = modified.add(prev.index)
            }
        }

        modified = modified.add(leg.index)
        updateGrid()
    }

    return {
        initDOM: function(childSelector) {
            legs = []

            _.each(container.children, (child, i) => {
                if (child.className.indexOf('ghost') !== -1) {
                    return
                }

                let x = parseInt(child.dataset.x || i)
                let width = parseInt(child.dataset.w || 1)
                let id = child.dataset.id

                let pixelWidth = width * columnWidth

                let newLeg = {
                    element: child,
                    x: x,
                    w: width,
                    xp: 0,
                    wp: pixelWidth,
                    index: i,
                    id: id
                }

                child.dataset.index = i
                child.style.width = `${pixelWidth}px`

                legs[newLeg.index] = newLeg
                attemptMove(newLeg, x)
            })

            interact(childSelector)
                .draggable({
                    onstart: function(evt) {
                        let leg = legs[evt.target.dataset.index]
                        ghost = buildGhost(leg)
                        leg.element.className += ' active'

                        modified = modified.clear()
                    },
                    onmove: function(evt) {
                        ghost.x += evt.dx

                        ghost.element.style.transform = `translateX(${ghost.x}px)`

                        // pidgeon hole to correct slot
                        let newX = Math.floor((ghost.x + columnWidth / 2) / columnWidth)

                        let leg = legs[ghost.origIndex]
                        if (leg.x !== newX) {
                            attemptMove(leg, newX)
                        }
                    },
                    onend: function() {
                        triggerMoveEvent(modified)
                        let leg = legs[ghost.origIndex]

                        leg.element.className = leg.element.className.replace('active', '')
                        ghost.element.remove()
                        ghost = null
                    }
                })
                .resizable({
                    edges: {
                        left: true,
                        right: true,
                    },
                    onstart: function(evt) {
                        let leg = legs[evt.target.dataset.index]
                        ghost = buildGhost(leg)
                        leg.element.className += ' active'

                        modified = modified.clear()
                    },
                    onmove: function(evt) {
                        let leg = legs[ghost.origIndex]

                        if (evt.edges.right) {
                            ghost.w += evt.deltaRect.right
                            ghost.element.style.width = `${ghost.w}px`

                            // pidgeon hole to correct slot
                            let newW = Math.floor((ghost.w + columnWidth / 2) / columnWidth)

                            if (leg.w !== newW) {
                                attemptResize(leg, newW)
                            }
                        } else if (evt.edges.left) {
                            ghost.x += evt.deltaRect.left
                            ghost.w = evt.rect.width

                            ghost.element.style.transform = `translateX(${ghost.x}px)`
                            ghost.element.style.width = `${ghost.w}px`

                            let newX = Math.floor((ghost.x + columnWidth / 2) / columnWidth)
                            let newW = Math.floor((ghost.w + columnWidth / 2) / columnWidth)

                            if (leg.x !== newX) {
                                attemptResize(leg, newW, newX)
                            }
                        }
                    },
                    onend: function(evt) {
                        triggerResizeEvent(modified)
                        let leg = legs[ghost.origIndex]

                        leg.element.className = leg.element.className.replace('active', '')
                        ghost.element.remove()
                        ghost = null
                    }
                })
        },

        size: function(containerWidth, columns) {
            container = _.isString(settings.selector) ? document.querySelector(settings.selector) : settings.selector

            settings.columns = columns
            columnWidth = containerWidth / columns

            // initialize the grid
            grid = _.times(columns, _.constant(EMPTY_SPACE))
        }
    }
}
